import { useCallback, useRef } from "react";
import { useApi } from "../api/ApiProvider";
import {
  BillPdfBatchItemDtoPdfTypeEnum,
  StartBillPdfBatchResponseDto,
  WorkflowResultDtoWorkflowNameEnum,
} from "../api-client";
import notificationStore from "../stores/Notification/notificationStore";
import { useWorkflow } from "./useWorkflow";

/**
 * Result from a single PDF generation job in the batch.
 */
export interface BillPdfJobResult {
  pdfType: BillPdfBatchItemDtoPdfTypeEnum;
  entityId: string;
  success: boolean;
  documentId?: string;
  blobUrl?: string;
  error?: string;
  durationMs: number;
}

/**
 * Aggregated result from the batch workflow.
 */
export interface BillPdfBatchResult {
  pdfType: BillPdfBatchItemDtoPdfTypeEnum;
  totalCount: number;
  successCount: number;
  failureCount: number;
  totalDurationMs: number;
  results: BillPdfJobResult[];
}

export interface UseBatchPrintPdfReturn {
  /**
   * Whether the batch workflow is running
   */
  isLoading: boolean;

  /**
   * Current progress (0-100)
   */
  progress: number;

  /**
   * Start a batch print workflow for bills.
   * Returns a promise that resolves with the batch result when complete.
   */
  startBatchPrint: (entityIds: string[], pdfDate?: string) => Promise<BillPdfBatchResult | null>;
}

/**
 * Hook for batch printing PDFs using the FlowProducer workflow.
 *
 * Uses useWorkflow for WebSocket subscription management.
 * All callbacks (onResult, onError, onProgress) are handled via the returned Promise.
 *
 * @param pdfType - The type of PDF to generate (GkvBill, PatientBill, etc.)
 *
 * @example
 * ```tsx
 * const { isLoading, progress, startBatchPrint } = useBatchPrintPdf(BillPdfBatchItemDtoPdfTypeEnum.GkvBill);
 *
 * const result = await startBatchPrint(["bill-1", "bill-2", "bill-3"]);
 * if (result) {
 *   console.log(`Generated ${result.successCount}/${result.totalCount} PDFs`);
 * }
 * ```
 */
export function useBatchPrintPdf(
  pdfType: BillPdfBatchItemDtoPdfTypeEnum = BillPdfBatchItemDtoPdfTypeEnum.GkvBill,
): UseBatchPrintPdfReturn {
  const { transaction } = useApi();

  const resultResolver = useRef<{
    resolve: (value: BillPdfBatchResult | null) => void;
  } | null>(null);

  const { workflowLoading, displayProgress, subscribeToWorkflow } = useWorkflow({
    workflowName: WorkflowResultDtoWorkflowNameEnum.BillPdfMultiStepWorkflow,
    onResult: (data) => {
      const jobResult = data as any;
      const batchResult: BillPdfBatchResult = {
        pdfType: jobResult?.data?.pdfType ?? pdfType,
        totalCount: jobResult?.data?.totalCount ?? 0,
        successCount: jobResult?.data?.successCount ?? 0,
        failureCount: jobResult?.data?.failureCount ?? 0,
        totalDurationMs: jobResult?.data?.totalDurationMs ?? 0,
        results: jobResult?.data?.results ?? [],
      };
      resultResolver.current?.resolve(batchResult);
    },
    onError: () => {
      resultResolver.current?.resolve(null);
    },
  });

  /**
   * Start batch print workflow for the given entity IDs.
   * Returns a promise that resolves when the workflow completes.
   */
  const startBatchPrint = useCallback(
    async (entityIds: string[], pdfDate?: string): Promise<BillPdfBatchResult | null> => {
      if (entityIds.length === 0) {
        notificationStore.showMessage("Keine Dokumente zum Drucken ausgewählt", "warning");
        return null;
      }

      try {
        // Start the batch workflow
        const response: StartBillPdfBatchResponseDto = await transaction.startBillPdfBatch({
          items: entityIds.map((entityId) => ({
            pdfType,
            entityId,
            pdfDate,
          })),
          workflowExpiry: 300, // 5 minutes
        });

        // Create a promise that will be resolved by the useWorkflow handler
        const resultPromise = new Promise<BillPdfBatchResult | null>((resolve) => {
          resultResolver.current = { resolve };
        });

        // Subscribe to workflow via useWorkflow
        subscribeToWorkflow({ workflowId: response.workflowId, message: response.message });

        return await resultPromise;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Fehler beim Starten der PDF-Generierung";
        notificationStore.showMessage(errorMessage, "error");
        return null;
      }
    },
    [transaction, subscribeToWorkflow, pdfType],
  );

  return { isLoading: workflowLoading, progress: displayProgress, startBatchPrint };
}
